import React from "react";
import ReactDOM from "react-dom";
import { ServicingOptions } from "../components/servicingOptions.js";

// Servicing for the for school basket add/edit/copy modals (editForSchoolBasketBody.html.twig).
// A full width "Servicing" row with a chevron expands the shared ServicingOptions list (the same one the checkout's
// servicing step uses).
// The ticked services live in the hidden forSchoolServices input, which stays disabled until the options have loaded
// (on first expand), so if they're never opened the server applies the servicing profile as before.
// The inputs deliberately aren't named basket[...] so the RFQ "reapply" code, which matches fields by name, can't overwrite them.

const servicesRequests = {};
let containerCount = 0;

function getServicesForCustomer(customerId) {
    const key = customerId || "none";
    if (!servicesRequests[key]) {
        servicesRequests[key] = $.ajax({
            url: "/basket/getForSchoolServices" + (customerId ? "/" + customerId : ""),
            dataType: "json"
        }).fail(() => {
            delete servicesRequests[key];
        });
    }
    return servicesRequests[key];
}

function getDialog(container) {
    return $(container).closest('dialog');
}

function getCustomerId($dialog) {
    return $dialog.find('[name="basketExtras[customerId]"]').val() || "";
}

function getSelectedServices(container) {
    const value = $(container).find('.forSchoolServicingInput').val();
    return value ? value.split(",").filter(id => id !== "") : [];
}

function setSelectedServices(container, serviceIds) {
    $(container).find('.forSchoolServicingInput').val(serviceIds.join(","));
    renderList(container);
}

// (Re)renders the shared list with the current ticks. Does nothing until the services have loaded.
function renderList(container) {
    if (!container.servicesGroups) {
        return;
    }

    ReactDOM.render(
        <ServicingOptions
            availableServices={container.servicesGroups}
            selectedServices={getSelectedServices(container)}
            wholeOrderLabel={container.dataset.wholeOrderLabel}
            idPrefix={container.dataset.idPrefix}
            wholeOrderLabelSelectName="forSchoolWholeOrderLabel"
            onChange={ids => {
                container.dataset.ticksChangedByHand = "1";
                setSelectedServices(container, ids);
            }}
            onWholeOrderLabelChange={wholeOrderLabel => { container.dataset.wholeOrderLabel = wholeOrderLabel; }}
        />,
        $(container).find('.forSchoolServicingOptions')[0]
    );
}

function statusContent(text) {
    return $('<div class="checkoutForm"></div>').append($('<p></p>').text(text));
}

function setStatus(container, text) {
    showContent(container, options => $(options).append(statusContent(text)));
}

// Swaps the options content using fill(optionsElement). While open, it waits for any slide to finish and then grows/shrinks
// smoothly to the new height (e.g. from the loading message to the full list) instead of jumping.
function showContent(container, fill) {
    const $options = $(container).find('.forSchoolServicingOptions');

    $options.promise().done(() => {
        const startHeight = $options.height();

        ReactDOM.unmountComponentAtNode($options[0]);
        $options.empty();
        fill($options[0]);

        if (!container.dataset.open) {
            return;
        }

        const endHeight = $options.css("height", "auto").height();

        $options.height(startHeight).animate({ height: endHeight }, 250, () => {
            $options.css("height", "");
        });
    });
}

function loadServices(container) {
    const customerId = getCustomerId(getDialog(container));
    container.dataset.customerId = customerId;

    getServicesForCustomer(customerId).done(groups => {
        // A later customer change wins if the responses come back out of order
        if (container.dataset.customerId !== customerId) {
            return;
        }

        if (!groups || groups.length === 0) {
            container.servicesGroups = null;
            setStatus(container, "No servicing options are available.");
            return;
        }

        container.servicesGroups = groups;
        showContent(container, () => renderList(container));
        $(container).find('.forSchoolServicingInput').prop("disabled", false);
        container.dataset.loaded = "1";
    }).fail(() => {
        if (container.dataset.customerId !== customerId) {
            return;
        }
        delete container.dataset.requested;
        setStatus(container, "Sorry, the servicing options couldn't be loaded. You can still set servicing at the checkout.");
    });
}

function toggleOptions(container) {
    const $options = $(container).find('.forSchoolServicingOptions');
    const opening = !container.dataset.open;

    if (opening) {
        container.dataset.open = "1";

        // Put the loading message in before the first slide so the panel opens with it, then grows when the list arrives
        if (!container.dataset.requested) {
            $options.empty().append(statusContent("Loading servicing options..."));
        }
    } else {
        delete container.dataset.open;
    }

    // Slide and fade together. stop() so quick repeated clicks don't queue up animations, and clear any height left by an
    // interrupted grow so the panel never reopens clipped.
    $options.stop(true).css("height", "").animate({
        height: opening ? "show" : "hide",
        opacity: opening ? "show" : "hide"
    }, 250);
    $(container).find('.forSchoolServicingToggle i')
        .toggleClass("fa-angle-down", !opening)
        .toggleClass("fa-angle-up", opening);
    $(container).find('.forSchoolServicingToggle').attr("title", opening ? "Hide servicing options" : "Show servicing options");

    // Load on first expand, so a page full of edit modals doesn't fire a request each
    if (opening && !container.dataset.requested) {
        container.dataset.requested = "1";
        loadServices(container);
    }
}

function initForSchoolBasketServicing($root) {
    $root.find('.forSchoolServicing').each(function () {
        if (this.dataset.initialised) {
            return;
        }
        this.dataset.initialised = "1";
        this.dataset.idPrefix = "fsServicing_" + (++containerCount) + "_";

        const container = this;
        const $dialog = getDialog(container);

        $(container).find('.forSchoolServicingToggle').on("click", () => {
            toggleOptions(container);
        });

        $dialog.find('.servicingProfileSelect').on("change", () => {
            applySelectedServicingProfile($dialog, true);
        });

        // Library users pick the school from a select rather than the customer search
        $dialog.find('select[name="basketExtras[customerId]"]').on("change", () => {
            reloadForSchoolServices($dialog);
        });
    });
}

// Called when the customer changes, as bespoke services depend on the customer
function reloadForSchoolServices($dialog) {
    $dialog.find('.forSchoolServicing').each(function () {
        if (this.dataset.loaded) {
            loadServices(this);
        }
    });
}

// Ticks the services of the picked servicing profile. "No profile" leaves the current ticks alone.
// pickedByUser is false when the customer's default profile is pre-selected after a school change, which mustn't wipe
// services already ticked by hand.
function applySelectedServicingProfile($dialog, pickedByUser = false) {
    const $select = $dialog.find('.servicingProfileSelect');
    const profiles = $select.data("profiles");
    const profile = profiles ? profiles[$select.val()] : null;

    if (!profile) {
        return;
    }

    $dialog.find('.forSchoolServicing').each(function () {
        if (!pickedByUser && this.dataset.ticksChangedByHand) {
            $select.val("none");
            return;
        }
        setSelectedServices(this, (profile.services || []).map(String));
    });
}

export { initForSchoolBasketServicing, reloadForSchoolServices, applySelectedServicingProfile };
